import type {
  LayerCalculation,
  ThermalLayer,
  UValueInput,
  UValueResult,
} from './types';

const EPSILON = 1e-9;

export function calculateLayerResistance(
  layer: ThermalLayer,
  conductivityWmK = layer.conductivityWmK,
): number {
  if (layer.ventilated) return 0;
  if (layer.resistanceOverrideM2KW !== undefined) {
    return Math.max(0, layer.resistanceOverrideM2KW);
  }
  if (!layer.thicknessMm || !conductivityWmK || conductivityWmK <= 0) return 0;
  return layer.thicknessMm / 1000 / conductivityWmK;
}

function equivalentBridgedResistance(
  materialResistance: number,
  bridgeResistance: number,
  bridgeFraction: number,
): number {
  const fraction = Math.min(1, Math.max(0, bridgeFraction));
  if (fraction <= EPSILON) return materialResistance;
  if (fraction >= 1 - EPSILON) return bridgeResistance;
  if (materialResistance <= EPSILON || bridgeResistance <= EPSILON) return 0;
  return 1 / ((1 - fraction) / materialResistance + fraction / bridgeResistance);
}

type HeatPath = { fraction: number; resistance: number };

function calculateWholePathLimit(
  layers: ThermalLayer[],
  surfaceResistance: number,
): number {
  let paths: HeatPath[] = [{ fraction: 1, resistance: surfaceResistance }];

  for (const layer of layers) {
    const materialResistance = calculateLayerResistance(layer);
    if (!layer.bridge || layer.ventilated) {
      paths = paths.map((path) => ({
        ...path,
        resistance: path.resistance + materialResistance,
      }));
      continue;
    }

    const bridgeFraction = Math.min(1, Math.max(0, layer.bridge.fraction));
    const bridgeResistance = calculateLayerResistance(
      layer,
      layer.bridge.conductivityWmK,
    );
    paths = paths.flatMap((path) => [
      {
        fraction: path.fraction * (1 - bridgeFraction),
        resistance: path.resistance + materialResistance,
      },
      {
        fraction: path.fraction * bridgeFraction,
        resistance: path.resistance + bridgeResistance,
      },
    ]);
  }

  const conductance = paths.reduce((sum, path) => {
    if (path.fraction <= EPSILON || path.resistance <= EPSILON) return sum;
    return sum + path.fraction / path.resistance;
  }, 0);

  return conductance > EPSILON ? 1 / conductance : 0;
}

export function calculateUValue(input: UValueInput): UValueResult {
  const surfaceResistance =
    Math.max(0, input.externalSurfaceResistanceM2KW) +
    Math.max(0, input.internalSurfaceResistanceM2KW) +
    Math.max(0, input.additionalResistanceM2KW ?? 0);

  const layers: LayerCalculation[] = input.layers.map((layer) => {
    const materialResistance = calculateLayerResistance(layer);
    const bridgeResistance = layer.bridge
      ? calculateLayerResistance(layer, layer.bridge.conductivityWmK)
      : undefined;
    const equivalentLayerResistance =
      bridgeResistance === undefined
        ? materialResistance
        : equivalentBridgedResistance(
            materialResistance,
            bridgeResistance,
            layer.bridge!.fraction,
          );

    return {
      id: layer.id,
      materialResistanceM2KW: materialResistance,
      bridgeResistanceM2KW: bridgeResistance,
      equivalentLayerResistanceM2KW: equivalentLayerResistance,
    };
  });

  const planeLimit =
    surfaceResistance +
    layers.reduce((sum, layer) => sum + layer.equivalentLayerResistanceM2KW, 0);
  const pathLimit = calculateWholePathLimit(input.layers, surfaceResistance);
  const upperResistance = Math.max(planeLimit, pathLimit);
  const lowerResistance = Math.min(planeLimit, pathLimit);
  const meanResistance = (upperResistance + lowerResistance) / 2;
  const valid = meanResistance > EPSILON && Number.isFinite(meanResistance);
  const baseUValue = valid ? 1 / meanResistance : 0;
  const correctionTotal = (input.correctionsWm2K ?? []).reduce(
    (sum, correction) => sum + Math.max(0, correction),
    0,
  );

  // Mirrors the recovered reference behaviour: ignore a combined correction
  // unless it exceeds three per cent of the uncorrected U-value.
  const correctionsApplied =
    correctionTotal > baseUValue * 0.03 ? correctionTotal : 0;
  const finalUValue = baseUValue + correctionsApplied;

  return {
    valid,
    layers,
    upperResistanceM2KW: upperResistance,
    lowerResistanceM2KW: lowerResistance,
    meanResistanceM2KW: meanResistance,
    baseUValueWm2K: baseUValue,
    correctionTotalWm2K: correctionTotal,
    correctionsAppliedWm2K: correctionsApplied,
    finalUValueWm2K: finalUValue,
    displayedUValueWm2K: Math.round((finalUValue + Number.EPSILON) * 100) / 100,
  };
}
